import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from "@mui/material";
import { useCallback, useMemo, type FC } from "react";
import { useFormContext } from "react-hook-form";
import { useDialog } from "../../../../utils/hooks/use-dialog";
import { ChannelSelect } from "../../../channels/components/channel-select";
import {
  isChatChannel,
  isEmailChannel,
  isTextChannel,
  type ChannelName,
} from "../../../channels/types";
import { prettyChannelName } from "../../../channels/utils";
import type { Tags } from "../../../merge-tag/types";
import type { MomentFormMoment } from "../../types/moment-form-moment";
import { useHandleReset } from "./hooks/useHandleReset";
import { useSelectedChannel } from "./hooks/useSelectedChannel";

export const Channel: FC<{
  channels: ChannelName[];
  disabled: boolean;
  newsletter?: boolean;
  mergeTags: Tags;
}> = ({ channels, disabled, newsletter, mergeTags }) => {
  const { reset } = useFormContext<MomentFormMoment>();
  const selectedChannel = useSelectedChannel();
  const handleReset = useHandleReset(newsletter);

  const { handleOpen, handleClose, ...confirmModal } = useDialog<ChannelName>();

  const availableChannels = useMemo(
    () => channels.filter((c) => (newsletter ? isEmailChannel(c) : true)),
    [channels, newsletter],
  );

  const handleSwitch = useCallback(
    async (name: ChannelName) => {
      // No current selection → just reset
      if (!selectedChannel) {
        if (name === "whatsapp") window.pendo?.track("whatsapp-channel-selected");
        await handleReset(name, mergeTags);
        return;
      }

      if (name === selectedChannel) return;
      if (name === "whatsapp") window.pendo?.track("whatsapp-channel-selected");

      const isSameChannelType =
        (isEmailChannel(selectedChannel) && isEmailChannel(name)) ||
        (isChatChannel(selectedChannel) && isChatChannel(name)) ||
        (isTextChannel(selectedChannel) && isTextChannel(name));

      if (isSameChannelType) {
        reset((values) => ({
          ...values,
          channel: name,
          additional_recipients: [],
        }));
      } else {
        handleOpen(name);
      }
    },
    [selectedChannel, handleReset, mergeTags, reset, handleOpen],
  );

  const handleConfirmChange = useCallback(
    async (name: ChannelName) => {
      await handleReset(name, mergeTags);
      handleClose();
    },
    [handleReset, mergeTags, handleClose],
  );

  const confirmText = useMemo(() => {
    if (!confirmModal.data) return null;

    const newChannel = prettyChannelName(confirmModal.data);

    return (
      <Stack spacing={1}>
        <DialogContentText>
          {`Changing channel to ${newChannel} will convert your message content and remove unsupported formatting, including column layouts, button styling, and some text formatting.`}
        </DialogContentText>
        <DialogContentText>This action cannot be undone</DialogContentText>
      </Stack>
    );
  }, [confirmModal.data]);

  return (
    <>
      <ChannelSelect
        channels={availableChannels}
        selectedChannel={selectedChannel}
        disabled={disabled}
        onSelect={handleSwitch}
      />

      <Dialog
        open={confirmModal.open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {`Change channel to ${prettyChannelName(confirmModal.data ?? null)}?`}
        </DialogTitle>
        <DialogContent>{confirmText}</DialogContent>
        <DialogActions>
          <Button data-analytics-id="channel-warning-dialog-cancel" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            data-analytics-id="channel-warning-dialog-confirm"
            type="submit"
            variant="outlined"
            onClick={() =>
              confirmModal.data && void handleConfirmChange(confirmModal.data).catch(console.error)
            }
          >
            Yes, I’m sure
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
